import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  AvatarProps,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Card,
  CardContent,
  CardMedia,
  Container,
  Divider,
  Grid,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import moment from 'moment';
import {
  getCurriculumsForHomePage,
  getCurriculumState,
  ICurriculumForHome,
} from '../../store/CurriculumSlice';
import { getBookmark, getBookmarkState } from '../../store/BookmarkSlice';
import { getMetaTagState } from '../../store/MetaTagSlice';
import { getUsageByUserId } from '../../store/AccessLimitSlice/AccessLimitApi';
import { getAuthState } from '../../store/AuthSlice';
import { getTrials } from '../../store/V2/TrialsSlice';
import { useAppSelector } from '../../hooks';
import {
  checkDiscountEligibility,
  checkUserPermission,
  getAppName,
  getUrlConvertedString,
  isMobileScreen,
  setClassToLocalStorage,
} from '../../helper';
import { TP_COLORS } from '../../constants';

import HomeHeader from './components/HomeHeader';
import ReviewCarousel from './components/ReviewCarousel';
import HomeFooter from './components/HomeFooter';
import Curriculums from './components/Curriculums';
import BasicLoader from '../../components/Loaders/BasicLoader';
import SearchBar from './components/SearchBar';
import AIMarkingSection from './components/AIMarkingSection';
import TrialClassBanner from './components/TrialClassBanner';
import TrialClassButton from './components/TrialClassButton';
import UpcomingClassBanner from './components/UpcomingClassBanner';
import {
  getScheduleState,
  getUserSchedulesAsUser,
  IScheduleShortEvent,
} from '../../store/V2/ScheduleSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../store/V2/RolesSlice';
import DiscountBanner from './components/DiscountBanner';
import { getConfigState } from '../../store/ConfigSlice';

interface IState {
  selectedCurriculum: ICurriculumForHome | undefined;
}

// DATA
const initialState: IState = {
  selectedCurriculum: undefined,
};

const greetingTypoProps: TypographyProps['sx'] = {
  component: 'p',
  variant: 'h5',
  fontWeight: 600,
  color: TP_COLORS.blue.light,
};

const greetingSubTypoProps: TypographyProps['sx'] = {
  component: 'p',
  variant: 'subtitle1',
  color: '#0F172A',
  style: { maxWidth: '430px' },
};

const stackStyles: StackProps['sx'] = {
  borderRadius: '20px',
  height: { xs: '170px', sm: '170px', md: '240px', lg: '240px' },
  flexDirection: { xs: 'row', sm: 'row', md: 'column', lg: 'column' },
  justifyContent: {
    xs: 'space-between',
    sm: 'space-between',
  },
};

const imageStackProps: StackProps['sx'] = {
  alignItems: 'flex-end',
  sx: {
    transition: 'all .2s ease-in',
    transformOrigin: 'bottom right',
    '&:hover': {
      transform: 'scale(1.15)',
    },
  },
};

const imageStyles = { width: '156px', height: '150px' };

const boxProps: BoxProps = {
  marginLeft: '30px',
  marginTop: '15px',
};

const buttonProps: ButtonProps = {
  fullWidth: true,
  size: 'large',
  variant: 'contained',
  color: 'inherit',
  sx: {
    borderRadius: '32px',
    backgroundColor: TP_COLORS.white,
    marginRight: '20px',
    marginTop: '30px',
  },
};

const titleProps: TypographyProps = {
  variant: 'h4',
  fontSize: '42px',
  fontWeight: '600',
};

const subTitleProps: TypographyProps = {
  component: 'h4',
  variant: 'h6',
  lineHeight: '1.1',
  color: '#475569',
};

const avatarStyles: AvatarProps['sx'] = {
  width: '30px',
  height: '30px',
};

const studentTitleProps: TypographyProps = {
  variant: 'h2',
  fontSize: isMobileScreen() ? '50px' : '80px',
  fontWeight: '600',
  color: TP_COLORS.tutopiyaGreenMain.light,
  marginBottom: '10px',
};

const studentSubTitleProps: TypographyProps = {
  component: 'h2',
  variant: isMobileScreen() ? 'h4' : 'h3',
  lineHeight: '1',
  color: TP_COLORS.blue.dark,
};

const studentDescriptionProps: TypographyProps = {
  fontSize: '18px',
  lineHeight: '1.3',
  color: TP_COLORS.tutopiyaGreenMain.light,
};

const featuredList = [
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/logo-money-fm.avif`,
    alt: 'logo-money-fm',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/logo-bt.avif`,
    alt: 'logo-bt',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/logo-dna.avif`,
    alt: 'logo-dna',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/logo-indvstrvs.avif`,
    alt: 'logo-indvstrvs',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/logo-cna.avif`,
    alt: 'logo-cna',
  },
  {
    src: `${process.env.PUBLIC_URL}/images/landingPage/logo-5.avif`,
    alt: 'logo-5',
  },
];

const filterOutExpiredUpcomingClasses = (item: IScheduleShortEvent) => {
  return (
    moment(item.start) > moment() ||
    (moment() >= moment(item.start) && moment() < moment(item.end))
  );
};

const sortUpcomingClasses = (a: IScheduleShortEvent, b: IScheduleShortEvent) =>
  new Date(a.start).getTime() - new Date(b.start).getTime();

const Home: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = isMobileScreen();

  // Store
  const curriculumState = useAppSelector(getCurriculumState);
  const bookmarkState = useAppSelector(getBookmarkState);
  const authState = useAppSelector(getAuthState);
  const metaTagState = useAppSelector(getMetaTagState);
  const scheduleState = useAppSelector(getScheduleState);
  const configState = useAppSelector(getConfigState);

  const authUser = authState.user;
  const { curriculumsForHomePage, status: curriculumStatus } = curriculumState;
  const { bookmark } = bookmarkState;

  // State
  const [state, setState] = useState<IState>(initialState);
  const [isJoinCalssBannerOpen, setJoinCalssBannerModal] = useState(false);

  const queryStringParams = new URLSearchParams(location.search);
  const urlCurriculum = queryStringParams.get('curriculum') || '';
  const urlSubject = queryStringParams.get('subject') || '';
  const urlGrade = queryStringParams.get('grade') || '';
  const urlTutor = queryStringParams.get('host') || '';
  const urlStudent = queryStringParams.get('participant') || '';
  const urlStart = queryStringParams.get('start') || '';
  const urlEnd = queryStringParams.get('end') || '';
  const urlMeeting = queryStringParams.get('meeting') || '';
  const currentHour = new Date().getHours();

  const userScheduleData = scheduleState?.userScheduleData?.list || [];

  const upcomingClass = userScheduleData.length
    ? userScheduleData
        .filter(filterOutExpiredUpcomingClasses)
        .sort(sortUpcomingClasses)[0]
    : undefined;

  const isDiscountEligible = checkDiscountEligibility();

  const greetingName =
    authUser && authUser.role.name !== 'Guest' ? `, ${authUser.firstname}` : '';

  const trialClassViewPermission = checkUserPermission(authUser, {
    trialClass: [USER_PERMISSION_INDIVIDUALS.TRIAL_CLASS_VIEW],
  });

  const trialClassCreatePermission =
    checkUserPermission(authUser, {
      trialClass: [USER_PERMISSION_INDIVIDUALS.TRIAL_CLASS_CREATE],
    }) && !configState.fromIGCSEBuddy;

  const discountBannerPermission = checkUserPermission(authUser, {
    headerContents: [
      USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DISCOUNT_BANNER,
    ],
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (curriculumsForHomePage.length === 0) {
      dispatch(getCurriculumsForHomePage());
    }

    if (urlTutor && urlStudent && urlStart && urlEnd && urlMeeting) {
      const classData = {
        tutorId: urlTutor,
        studentId: urlStudent,
        subject: urlSubject,
      };
      setClassToLocalStorage({
        ...classData,
        start: urlStart,
        end: urlEnd,
        meetingId: urlMeeting,
      });
      dispatch(getBookmark(classData));
    }

    if (authUser) {
      dispatch(getTrials());

      if (authUser.role.name === 'Student' || authUser.role.name === 'Guest') {
        dispatch(getUsageByUserId(authUser._id));
      }

      if (authUser && authUser.role.name !== 'Guest') {
        dispatch(
          getUserSchedulesAsUser({
            userId: authUser._id,
            startDate: moment().subtract(3, 'hour').toISOString(),
            endDate: moment().add(5, 'hour').toISOString(),
          })
        );
        setJoinCalssBannerModal(true);
      }
    }

    // To remove the last '/' mark from URL
    if (location.pathname.slice(-1) === '/') {
      navigate(location.pathname.slice(0, -1));
    }
  }, []);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      const curriculum = (params.curriculum || 'cambridge_igcse').trim();
      const _index = curriculumsForHomePage.findIndex(
        (curriculumForHomePage) =>
          getUrlConvertedString(curriculumForHomePage.name) ===
          curriculum.trim()
      );

      let selectedCurriculum: IState['selectedCurriculum'];

      if (_index >= 0) {
        selectedCurriculum = curriculumsForHomePage[_index];
      }

      if (urlCurriculum && urlSubject && urlGrade) {
        const selectedCurriculumItem = curriculumsForHomePage.find(
          (item) => item.name === urlCurriculum
        );

        if (
          selectedCurriculumItem &&
          selectedCurriculumItem.subjects &&
          selectedCurriculumItem.subjects.length
        ) {
          const selectedSubjectItem = selectedCurriculumItem.subjects.find(
            (item) => item.name === urlSubject && item.grade === urlGrade
          );

          if (selectedSubjectItem) {
            const urlConvertedSubject = getUrlConvertedString(
              selectedSubjectItem.name
            );

            const urlConvertedCurriculum = getUrlConvertedString(urlCurriculum);
            const urlConvertedTopic = getUrlConvertedString(
              selectedSubjectItem.subjectLevel
            );
            let urlConvertedSubTopic = getUrlConvertedString(
              selectedSubjectItem.subTopic && selectedSubjectItem.subTopic.name
                ? selectedSubjectItem.subTopic.name
                : ''
            );

            let subTopicId =
              selectedSubjectItem.subTopic && selectedSubjectItem.subTopic._id
                ? selectedSubjectItem.subTopic._id
                : '';

            if (bookmark.subTopicId && bookmark.subTopicName) {
              urlConvertedSubTopic = getUrlConvertedString(
                bookmark.subTopicName
              );
              subTopicId = bookmark.subTopicId;
            }

            navigate(
              `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicId}`
            );
          }
        }
      }

      setState((prevState) => ({
        ...prevState,
        selectedCurriculum,
      }));
    }
  }, [curriculumsForHomePage, bookmarkState.bookmark, params.curriculum]);

  const getGreeting = () => {
    if (currentHour < 12) return 'Morning';
    if (currentHour >= 12 && currentHour <= 16) return 'Afternoon';
    return 'Evening';
  };

  let metaTitle = `IGCSE And GCSE - Maths And Science Questions with Answers By Topic | ${getAppName()}`;

  let metaDesc =
    'Science and Maths questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!';

  if (state.selectedCurriculum) {
    metaTitle = `${
      state.selectedCurriculum.name || ''
    } - Maths And Science Questions with Answers By Topic | ${getAppName()}`;

    metaDesc = `${
      state.selectedCurriculum.name || ''
    } maths and science questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!`;
  }

  // const pastPaperVersion = 3;
  // const releaseNoteShowed = getReleaseNoteShowedInHomeFromLocalStorage();
  // const [isPastPaperReleaseNoteOpen, setPastPaperReleaseNote] = useState(() => {
  //   return (
  //     !releaseNoteShowed || parseFloat(releaseNoteShowed) < pastPaperVersion
  //   );
  // });

  return (
    <Box sx={{ backgroundColor: '#E5F7F666' }}>
      {!metaTagState.metaTags ||
        (!metaTagState.metaTags[location.pathname.replace(/[/]$/, '')] && (
          <Helmet>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
          </Helmet>
        ))}

      <HomeHeader />

      {upcomingClass && (
        <UpcomingClassBanner
          open={isJoinCalssBannerOpen}
          onClose={() => setJoinCalssBannerModal(false)}
          classData={upcomingClass}
        />
      )}

      {((authUser && discountBannerPermission) || !authUser) &&
      isDiscountEligible &&
      !upcomingClass ? (
        <DiscountBanner />
      ) : null}

      <div id="home-container" style={{ minHeight: '80vh' }}>
        <Container maxWidth="lg" sx={{ mt: '40px', mb: '30px', pt: '40px' }}>
          {/* Greeting Section */}
          <Stack
            component="section"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/landingPage/${
                  currentHour <= 16 ? 'morning' : 'evening'
                }.avif`}
                alt="morning"
                style={{ width: '238px', height: '180px' }}
              />
              <Box marginTop="20px">
                <Typography {...greetingTypoProps}>
                  Good {getGreeting()}
                  {greetingName}
                </Typography>

                <Typography id="top-of-search" {...greetingSubTypoProps}>
                  Access Comprehensive Study Resources and Over 50,000 Practice
                  Questions.Perfect Your Exam Preparation with {getAppName()}.
                </Typography>
              </Box>
            </Box>
            {trialClassViewPermission && <TrialClassButton />}
          </Stack>

          <SearchBar />

          {curriculumStatus === 'loading' ||
          bookmarkState.status === 'loading' ? (
            <BasicLoader
              containerProps={{ minHeight: '362px', alignItems: 'center' }}
            />
          ) : (
            <Curriculums
              curriculumsForHomePage={curriculumsForHomePage}
              selectedCurriculum={state.selectedCurriculum}
            />
          )}

          <Divider style={{ margin: '40px 0' }} />

          {/* Detailed Marking Scheme */}
          <AIMarkingSection />

          <Divider style={{ margin: '40px 0' }} />

          {/* Explore Tutopiya */}
          <Box component="section">
            <Typography
              component="h3"
              variant="h5"
              marginBottom="30px"
              textAlign={isMobile ? 'center' : 'left'}
            >
              Explore {getAppName()}
            </Typography>
            <Grid container alignItems="flex-start" spacing={2}>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Stack
                  sx={{
                    ...stackStyles,
                    background: `url('${process.env.PUBLIC_URL}/images/landingPage/blue-shape.avif') center/cover no-repeat, #C6DEF166`,
                  }}
                >
                  <Box {...boxProps}>
                    <Typography {...titleProps} color="#1196FF">
                      200+
                    </Typography>
                    <Typography {...subTitleProps}>Subject Videos</Typography>
                  </Box>
                  <Stack {...imageStackProps}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/landingPage/video-camera.avif`}
                      alt="video-camera"
                      style={imageStyles}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Stack
                  sx={{
                    ...stackStyles,
                    background: `url('${process.env.PUBLIC_URL}/images/landingPage/yellow-shape.avif') center/cover no-repeat, #FAEDCB`,
                  }}
                >
                  <Box {...boxProps}>
                    <Typography {...titleProps} color="#FFBE13">
                      50000+
                    </Typography>
                    <Typography {...subTitleProps}>
                      Practice Questions
                    </Typography>
                  </Box>
                  <Stack {...imageStackProps}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/landingPage/question-mark.avif`}
                      alt="question-mark"
                      style={imageStyles}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Stack
                  sx={{
                    ...stackStyles,
                    background: `url('${process.env.PUBLIC_URL}/images/landingPage/pink-shape.avif') center/cover no-repeat, #F2C6DE`,
                  }}
                >
                  <Box {...boxProps}>
                    <Typography {...titleProps} color="#EC3B9C">
                      100+
                    </Typography>
                    <Typography {...subTitleProps}>Learning Hours</Typography>
                  </Box>
                  <Stack {...imageStackProps}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/landingPage/clock.avif`}
                      alt="clock"
                      style={imageStyles}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3}>
                <Stack
                  sx={{
                    ...stackStyles,
                    background: `url('${process.env.PUBLIC_URL}/images/landingPage/purple-shape.avif') center/cover no-repeat, #DBCDF0`,
                  }}
                >
                  <Box {...boxProps}>
                    <Typography {...titleProps} color="#8640F0">
                      AI
                    </Typography>
                    <Typography {...subTitleProps}>Assistance</Typography>
                  </Box>
                  <Stack {...imageStackProps}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/landingPage/ai.avif`}
                      alt="ai"
                      style={imageStyles}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider style={{ margin: '40px 0' }} />
        </Container>

        {/* <PastPapersReleaseNote
          open={isPastPaperReleaseNoteOpen}
          onClose={() => {
            setPastPaperReleaseNote(false);
            setReleaseNoteShowedInHomeToLocalStorage(
              pastPaperVersion.toString()
            );
          }}
        /> */}

        {/* Book Trial Class */}
        {trialClassCreatePermission && <TrialClassBanner />}

        <Container maxWidth="lg" sx={{ mt: '50px', mb: '60px' }}>
          {/* Our Star Student */}
          <Box component="section" paddingBottom="30px">
            <Grid container>
              <Grid item xs={12} sm={12} md={5} lg={5} paddingRight="16px">
                <Typography {...studentSubTitleProps}>Our Star</Typography>
                <Typography {...studentTitleProps}>Students</Typography>
                <Typography {...studentDescriptionProps}>
                  We&apos;re happy to see more than a thousand happy students
                  that have used {getAppName()} to practice before important
                  exams. Are you next ?
                </Typography>
                <Stack flexDirection={isMobile ? 'column' : 'row'}>
                  <Button
                    {...buttonProps}
                    href="https://www.trustpilot.com/review/tutopiya.com"
                    endIcon={
                      <Avatar
                        sx={avatarStyles}
                        alt="star-icon"
                        src={`${process.env.PUBLIC_URL}/images/landingPage/star-icon.avif`}
                      />
                    }
                  >
                    View more from
                  </Button>
                  <Button
                    {...buttonProps}
                    href="https://g.page/r/CR92vM9Qj6KlEAI/review"
                    endIcon={
                      <Avatar
                        sx={avatarStyles}
                        alt="google-icon"
                        src={`${process.env.PUBLIC_URL}/images/landingPage/google-icon.avif`}
                      />
                    }
                  >
                    View more from
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={12} md={7} lg={7}>
                <ReviewCarousel />
              </Grid>
            </Grid>
          </Box>

          <Divider style={{ margin: '40px 0' }} />

          <Typography component="h3" variant="h5" textAlign="center">
            As Featured On
          </Typography>
        </Container>

        {/* As Featured On */}
        <Box component="section" sx={{ backgroundColor: '#E5F7F6' }}>
          <Container maxWidth="lg">
            <Grid container>
              {featuredList.map((element, index) => (
                <Grid
                  key={index}
                  item
                  xs={6}
                  sm={4}
                  md={2}
                  lg={2}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={element.src}
                    alt={element.alt}
                    style={{
                      width: '153px',
                      height: element.alt === 'logo-dna' ? '87px' : '156px',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ mt: '50px', mb: '60px' }}>
          {/* Meet Our Experts */}
          <Box component="section" padding="30px 0px">
            <Grid container>
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <Typography {...studentSubTitleProps}>Meet Our</Typography>
                <Typography {...studentTitleProps}>Experts</Typography>
                <Typography {...studentDescriptionProps}>
                  Meet our incredible team of experts with decades of
                  experience! They&apos;re here to make your academic journey a
                  success, offering guidance and support along the way.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={7} lg={7}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-evenly"
                  marginTop="24px"
                >
                  <Card elevation={2} sx={{ borderRadius: '20px' }}>
                    <CardMedia
                      sx={{
                        minHeight: '250px',
                        backgroundColor: '#97c0c9',
                        margin: '10px',
                        borderRadius: '10px',
                      }}
                      image={`${process.env.PUBLIC_URL}/images/landingPage/img-nuha.avif`}
                      title="Nuha G"
                    />
                    <CardContent
                      sx={{
                        padding: '0px 12px',
                        '&:last-child': {
                          paddingBottom: '15px',
                        },
                      }}
                    >
                      <Typography fontSize="18px" fontWeight="600">
                        Nuha G
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Head of Academics
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        18+ years teaching experience
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card elevation={2} sx={{ borderRadius: '20px' }}>
                    <CardMedia
                      sx={{
                        minHeight: '250px',
                        backgroundColor: '#F6D27C',
                        margin: '10px',
                        borderRadius: '10px',
                      }}
                      image={`${process.env.PUBLIC_URL}/images/landingPage/img-fairooza.avif`}
                      title="Fairooza F"
                    />
                    <CardContent
                      sx={{
                        padding: '0px 12px',
                        '&:last-child': {
                          paddingBottom: '15px',
                        },
                      }}
                    >
                      <Typography fontSize="18px" fontWeight="600">
                        Fairooza F
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Head of Maths
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        15+ years teaching experience
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </div>

      <HomeFooter />
    </Box>
  );
};

export default Home;
